import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import success from '../../../assets/images/backoffice/kyc6.png';
import { CreditCard } from '../../../components/common/CreditCard';
import { Hub } from '../../../components/common/Hub/Hub';
import {
  copyLink,
  formatUSD,
  showNetworkType,
  showTokenType,
  USDT
} from '../../../helpers/helperFunctions';
import { ORDERS } from '../../../routes/routes';
import { CaptionText, H2, H3, P3 } from '../../../styles/FontStyles';
import { Button, Flexed } from '../../../styles/GlobalComponents';
import { color } from '../../../styles/GlobalStyles';
import { Icon, IconCopy } from '../../../styles/Icons';
import { NetworkType } from '../../../types/types';
import { SelectedUSDT } from '../Marketplace.styled';

interface SuccessProps {
  selectedToken: any;
  selectedPackage: any;
  selectedLicence: any;
  calculatedPrice: string;
}

export const Success = ({
  selectedPackage,
  calculatedPrice,
  selectedLicence
}: SuccessProps) => {
  const { t } = useTranslation('marketplace');
  const history = useHistory();

  const { userPackage: userPackagee, subscription } = useAppSelector(
    (state) => state.marketplace
  );

  const {
    image,
    name,
    price,
    weeklyCap,
    packageId,
    limitedQuantity,
    calculator,
    tradingBinaryPercentage,
    willyMemeCoins,
    subtitle,
    tickets,
    subscriptionPrice
    // eslint-disable-next-line no-unsafe-optional-chaining
  } = selectedPackage;

  const amountToPay = selectedLicence
    ? subscriptionPrice
      ? selectedLicence.price + price + subscriptionPrice
      : selectedLicence.price + price
    : subscriptionPrice
    ? subscriptionPrice + price
    : price;

  const userPackage = userPackagee || subscription;

  const isWithTether = userPackage?.currencyType === 1;

  return (
    <>
      {!isWithTether && (
        <>
          <Flexed justifyCenter>
            <img src={success} alt='success' />
          </Flexed>
          <H3 center mb='5' color={color.colors.neutral900}>
            {t('order_successfully_purchased')}
          </H3>
        </>
      )}

      {isWithTether && (
        <>
          <P3 mb='8' color={color.colors.neutral900}>
            {t('success.you_selected')}
            <br />
          </P3>
          <Hub
            key={packageId}
            condensed
            showTickets={packageId >= 170 && packageId < 180}
            image={image}
            title={name}
            weeklyCap={weeklyCap}
            limitedQuantity={limitedQuantity}
            dobuleTradingBinaries={tradingBinaryPercentage}
            price={subscriptionPrice ? price : amountToPay}
            subscriptionPrice={subscriptionPrice}
            selectedLicence={selectedLicence}
            packagePrice={price}
            includesSubscription={selectedLicence?.access}
            willyMemeCoins={willyMemeCoins}
            subtitle={subtitle}
            tickets={tickets}
            info={
              <>
                {calculator && (
                  <Flexed alignEnd end>
                    <H2 bold mr='16' alignEnd>
                      {calculator}
                    </H2>
                    <CaptionText bold right mb='4'>
                      {t('reward')}
                      <br /> {t('multiplicator')}
                    </CaptionText>
                  </Flexed>
                )}
              </>
            }
          />

          <P3 mb='8' mt='20' color={color.colors.neutral900}>
            {t('success.text1')}
            <br />
            <br />
            {t('success.text2')}
            <br />
            <br />
            {t('success.text3')}
          </P3>

          <div style={{ marginBottom: 20 }}>
            <P3 bold mb='8' color={color.colors.neutral900}>
              {t('success.payment_amount')}
            </P3>
            <SelectedUSDT>
              <div className='truncated'>
                <div>
                  {' '}
                  {calculatedPrice
                    ? formatUSD(Number(calculatedPrice))
                    : formatUSD(amountToPay)}{' '}
                </div>
              </div>
            </SelectedUSDT>
          </div>

          <div style={{ marginBottom: 20 }}>
            <P3 bold mb='8' color={color.colors.neutral900}>
              {t('selected_network')}
            </P3>
            <SelectedUSDT>
              <div className='truncated'>
                <div>{showNetworkType(userPackage ?? subscription)}</div>
              </div>
            </SelectedUSDT>
          </div>

          <div style={{ marginBottom: 20 }}>
            <P3 bold mb='8' color={color.colors.neutral900}>
              Selected Token
            </P3>
            <SelectedUSDT>
              <div className='truncated'>
                <div>
                  {userPackage?.networkCurrencyType
                    ? userPackage?.networkCurrencyType
                    : showTokenType(userPackage)}
                </div>
              </div>
            </SelectedUSDT>
          </div>

          <P3 bold mb='8' color={color.colors.neutral900}>
            {t('success.wallet_address')}
          </P3>

          <SelectedUSDT
            onClick={() => {
              copyLink(userPackage?.paymentAddress, t('success.copied'));
            }}
            style={{ cursor: 'pointer' }}>
            <div className='truncated'>
              <div>
                {userPackage?.paymentAddress ?? subscription?.paymentAddress}
              </div>
            </div>

            <Icon ml='10' color={color.colors.neutral900}>
              <IconCopy />
            </Icon>
          </SelectedUSDT>

          {userPackage?.networkType === NetworkType.TRC_20 && (
            <CreditCard
              paymentAddress={userPackage?.paymentAddress}
              amount={price}
            />
          )}
        </>
      )}

      <Flexed justifyCenter mt='40'>
        <Button
          onClick={() => {
            history.push(ORDERS);
          }}>
          {t('close_button')}
        </Button>
      </Flexed>
    </>
  );
};
